import { client } from "@/hono";
import { useCitadel } from "@/hooks/use-citadel";
import { useWikiPage } from "@/hooks/use-wiki-page";
import { Button } from "@msuite/ui/button";
import { Icon, icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Menu, type MenuItems } from "@msuite/ui/menu";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import React from "react";
import { useNavigate } from "react-router";

/** Props Interface */
interface WikiPageActionsProps {
	spaceId: string;
	pageId: string;
	onEdit: () => void;
}

/**
 * The page's actions, top right of the document: editing as the one visible button, copying and
 * deleting behind the menu. Each is shown only to a caller the Wiki rule lets do it, so the
 * header never offers a button the server would refuse.
 */
export function WikiPageActions({ spaceId, pageId, onEdit }: WikiPageActionsProps) {
	/** Hooks */
	const { t } = useTranslation();
	const navigate = useNavigate();
	const qc = useQueryClient();
	const deletePageModal = useAsyncModal();
	const { citadel } = useCitadel();

	/** Query */
	// The same query the page holds, so this is a cache read: the rule binds on the organisation
	// the row belongs to, and that is on the row.
	const { wikiPage } = useWikiPage({ spaceId, pageId });

	/** Functions */
	async function handleOnCopyPage() {
		const response = await client.wiki.spaces[":spaceId"].pages[":pageId"].copy.$post({
			param: { spaceId, pageId },
		});
		const data = await responseHandler(response);
		await qc.invalidateQueries({ predicate: invalidate(["wiki-spaces", spaceId, "pages"]) });
		if (data.page) navigate(`/wiki/${spaceId}/${data.page.id}`);
	}

	async function handleOnDeletePage() {
		const isConfirmed = await deletePageModal.confirm();
		if (!isConfirmed) return;
		const response = await client.wiki.spaces[":spaceId"].pages[":pageId"].$delete({
			param: { spaceId, pageId },
		});
		await responseHandler(response);
		await qc.invalidateQueries({ predicate: invalidate(["wiki-spaces", spaceId, "pages"]) });
		navigate(`/wiki/${spaceId}`);
	}

	/** Computed */
	const resource = wikiPage?.orgId ? { id: wikiPage.id, orgId: wikiPage.orgId } : null;
	const canUpdate = !!resource && citadel?.can("update", "Wiki", resource) === true;
	const canCreate = !!resource && citadel?.can("create", "Wiki", resource) === true;
	const canDelete = !!resource && citadel?.can("delete", "Wiki", resource) === true;
	const menuItems: MenuItems = [
		...(canCreate
			? [{ icon: icon("Copy"), label: t("wiki.copy_page"), onClick: handleOnCopyPage }]
			: []),
		...(canCreate && canDelete ? (["divider"] as const) : []),
		...(canDelete
			? [
					{
						icon: icon("Trash2"),
						label: t("wiki.delete_page"),
						onClick: handleOnDeletePage,
						isDanger: true,
					},
				]
			: []),
	];

	/** Render */
	return (
		<React.Fragment>
			<div className="h-stack shrink-0">
				{menuItems.length > 0 && (
					<Menu items={() => menuItems}>
						<Button icon={<Icon name="EllipsisVertical" />} />
					</Menu>
				)}
				{canUpdate && (
					<Button
						variant="primary"
						icon={<Icon name="Edit" />}
						onClick={onEdit}
					>
						{t("general.edit")}
					</Button>
				)}
			</div>
			<ConfirmModal
				controller={deletePageModal}
				title={t("wiki.delete_page")}
				message={t("wiki.are_you_sure_you_want_to_delete_this_page_and_all_its_content")}
			/>
		</React.Fragment>
	);
}
